import { z } from 'zod';

/**
 * Équipes et chefs d'équipe (commissions d'encadrement).
 *
 * Un membre d'équipe touche sa commission / rémunération DIMINUÉE de la part du chef d'équipe ;
 * cette part est attribuée automatiquement au chef (qui peut être superviseur, commercial ou
 * poseur). Exemple : barème 100 € ; hors équipe → 100 € ; membre d'une équipe à 20 % → 80 € pour
 * le membre + 20 € pour le chef.
 *
 * Confidentialité : le membre ne voit JAMAIS la part du chef ni l'existence du partage — partout
 * où un montant lui est montré (catalogue, lignes de dossier, relevés, exports), c'est son montant
 * net. Seuls le chef (ses propres lignes d'encadrement) et les responsables voient le partage.
 */
export interface TeamRecord {
  id: string;
  name: string;
  leaderId: string;
  leaderName: string;
  /** part du chef sur les commissions de VENTE des membres (points de base : 2000 = 20 %) */
  saleShareBp: number;
  /** part du chef sur les rémunérations de POSE / ENTRETIEN des membres */
  installShareBp: number;
  isActive: boolean;
  members: { userId: string; name: string; since: string }[];
}

export const teamInput = z.object({
  name: z.string().trim().min(2, 'Nom obligatoire').max(80),
  leaderId: z.string().uuid(),
  saleShareBp: z.number().int().min(0).max(10_000),
  installShareBp: z.number().int().min(0).max(10_000),
  isActive: z.boolean().default(true),
});
export const teamPatch = teamInput.partial();
export const teamMembersInput = z.array(z.string().uuid()).max(500);

/** Partage d'un montant : la part du chef est arrondie au centime, le membre reçoit le reste. */
export function splitAmount(amountCents: number, shareBp: number): { member: number; leader: number } {
  const leader = Math.round((amountCents * shareBp) / 10_000);
  return { member: amountCents - leader, leader };
}

/** Barème montré à un membre d'équipe : déjà net de la part du chef. */
export function netRule<T extends { saleCommissionType: 'none' | 'fixed' | 'percent' | null; saleCommissionValue: number | null; installRemunerationCents: number | null }>(
  p: T,
  share: { saleBp: number; installBp: number } | null | undefined,
): T {
  if (!share || (!share.saleBp && !share.installBp)) return p;
  return {
    ...p,
    saleCommissionValue: p.saleCommissionValue === null ? null : splitAmount(p.saleCommissionValue, share.saleBp).member,
    installRemunerationCents: p.installRemunerationCents === null ? null : splitAmount(p.installRemunerationCents, share.installBp).member,
  };
}

export const formatShare = (bp: number) => `${(bp / 100).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} %`;
